'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from '@/i18n/routing';
import {
  clearActiveLiveCallSession,
  getLiveDetailIdFromPathname,
  triggerActiveLiveCallSessionLeave,
} from '../utils/live-call-session';

export const LiveSessionLifecycleWatcher = ({ liveId }: { liveId?: string }) => {
  const pathname = usePathname();
  const currentPathnameRef = useRef(pathname);
  const currentLiveIdRef = useRef<string | null>(liveId ?? getLiveDetailIdFromPathname(pathname));

  const triggerLeave = (liveId: string, reason: 'route_change_leave' | 'pagehide_leave') => {
    triggerActiveLiveCallSessionLeave({ liveId, reason });

    clearActiveLiveCallSession(liveId);
    currentLiveIdRef.current = null;
  };

  useEffect(() => {
    const nextLiveId = liveId ?? getLiveDetailIdFromPathname(pathname);
    const previousLiveId = currentLiveIdRef.current;
    const pathnameChanged = currentPathnameRef.current !== pathname;

    if (previousLiveId && (pathnameChanged || previousLiveId !== nextLiveId)) {
      triggerLeave(previousLiveId, 'route_change_leave');
    }

    currentPathnameRef.current = pathname;
    currentLiveIdRef.current = nextLiveId;
  }, [liveId, pathname]);

  useEffect(() => {
    const handlePopState = () => {
      const liveId = currentLiveIdRef.current;
      if (!liveId) {
        return;
      }

      triggerLeave(liveId, 'route_change_leave');
    };

    const handlePageHide = () => {
      const liveId = currentLiveIdRef.current;
      if (!liveId) {
        return;
      }

      triggerLeave(liveId, 'pagehide_leave');
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);

  return null;
};
